<template>
  <div class="release-date-container">
    <h4 v-if="releaseDate && isModule" class="release-date">
      Release on: <i :class="dateIsPast(releaseDate) ? 'green' : 'amber'">{{ formatDateTimeString(releaseDate) }}</i>
      <k-tooltip text="Content in the module becomes available to students at midnight (UTC) on this date" trigger-label="More information about release date">
        <i class="fas fa-info-circle" aria-hidden="true"></i>
      </k-tooltip>
    </h4>
    <h4 v-if="expectedCompletionDate && isModule" class="release-date">
      Due by: <i :class="dateIsPast(expectedCompletionDate) ? 'amber' : 'green'">{{ formatDateTimeString(expectedCompletionDate) }}</i>
      <k-tooltip text="Content in the module is expected to have been completed by midnight (UTC) on this date" trigger-label="More information about completion date">
        <i class="fas fa-info-circle" aria-hidden="true"></i>
      </k-tooltip>
    </h4>
    <h4 v-if="releaseAfterDays != null && !isModule" class="release-date">
      Release on <i class="green">day {{ releaseOnDay }}</i>
      <k-tooltip :text="`Content in the module becomes available to students on programme day ${releaseOnDay}`" trigger-label="More information about release on">
        <i class="fas fa-info-circle" aria-hidden="true"></i>
      </k-tooltip>
    </h4>
    <h4 v-if="expectedCompletionDays != null && !isModule" class="release-date">
      Duration - <i class="amber">{{ durationDaysText }}</i>
      <k-tooltip :text="`Content in the module is expected to take ${durationDaysText} to complete after the module is released`" trigger-label="More information about duration">
        <i class="fas fa-info-circle" aria-hidden="true"></i>
      </k-tooltip>
    </h4>
    <h4 v-if="moduleDaysRangeText && !isModule" class="release-date">
      <i class="green">{{ moduleDaysRangeText }}</i>
      <k-tooltip :text="`The module runs from programme day ${moduleStartDay} to programme day ${moduleEndDay}, inclusive`" trigger-label="More information about module days">
        <i class="fas fa-info-circle" aria-hidden="true"></i>
      </k-tooltip>
    </h4>
  </div>
</template>
<style scoped>
.release-date-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.release-date-container .release-date {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.release-date-container .release-date:nth-child(1n) {
  border-right: var(--border-primary);
  padding-right: 15px;
}

.release-date-container .release-date:last-child {
  border-right: 0;
}
</style>
<script>
import TimeMixins from '@mixins/time-mixins';
import PluralMixin from '@mixins/plural-mixin';
import { KTooltip } from '@kate/components';
import useReleaseModuleStore from '@stores/release-module-store';

export default {
  components: {
    KTooltip,
  },
  mixins: [TimeMixins, PluralMixin],
  props: {
    module: {
      type: Object,
      required: true,
    },
    isModule: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      releaseModuleStore: useReleaseModuleStore(),
    };
  },
  computed: {
    releaseAfterDays() {
      return this.releaseModuleStore.getModuleValue(this.module.index, 'release_after_days');
    },
    expectedCompletionDays() {
      return this.releaseModuleStore.getModuleValue(this.module.index, 'expected_completion_days');
    },
    releaseDate() {
      return this.releaseModuleStore.getModuleValue(this.module.index, 'release_date');
    },
    expectedCompletionDate() {
      return this.releaseModuleStore.getModuleValue(this.module.index, 'expected_completion_date');
    },
    durationDaysText() {
      return this.pluralize('day', this.expectedCompletionDays, true);
    },
    releaseOnDay() {
      return this.releaseAfterDays + 1;
    },
    moduleStartDay() {
      if (this.releaseAfterDays == null) {
        return null;
      }
      return this.releaseOnDay;
    },
    moduleEndDay() {
      if (this.releaseAfterDays == null || this.expectedCompletionDays == null) {
        return null;
      }
      if (this.expectedCompletionDays === 0) {
        return this.moduleStartDay;
      }
      return this.releaseAfterDays + this.expectedCompletionDays;
    },
    moduleDaysRangeText() {
      if (this.moduleStartDay == null || this.moduleEndDay == null) {
        return null;
      }
      return `Days ${this.moduleStartDay} - ${this.moduleEndDay} (inclusive)`;
    },
  },
  methods: {
    formatDateTimeString(dateString) {
      if (dateString) {
        return this.parseTimestamp(this.getDate(dateString), undefined, 'MMM d, yyyy HH:mm');
      }
      return '';
    },
  },
};
</script>
